import { PRODUCT_STATUS_CONFIG, normalizeProductStatus } from './constants.js';

const BILLABLE_STATUSES = new Set(['LIVE', 'PENDING CANCEL']);
const MONTHS = new Map([
  ['JAN', 0],
  ['JANUARY', 0],
  ['FEB', 1],
  ['FEBRUARY', 1],
  ['MAR', 2],
  ['MARCH', 2],
  ['APR', 3],
  ['APRIL', 3],
  ['MAY', 4],
  ['JUN', 5],
  ['JUNE', 5],
  ['JUL', 6],
  ['JULY', 6],
  ['AUG', 7],
  ['AUGUST', 7],
  ['SEP', 8],
  ['SEPT', 8],
  ['SEPTEMBER', 8],
  ['OCT', 9],
  ['OCTOBER', 9],
  ['NOV', 10],
  ['NOVEMBER', 10],
  ['DEC', 11],
  ['DECEMBER', 11],
]);

export const booleanOrNull = (value) => {
  if (value === true || value === false) return value;
  const normalized = String(value ?? '').trim().toLowerCase();
  if (['true', 'yes', 'y', '1'].includes(normalized)) return true;
  if (['false', 'no', 'n', '0'].includes(normalized)) return false;
  return null;
};

const parseDateOnly = (value) => {
  if (!value) return null;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }
  const text = String(value).trim();
  if (!text) return null;
  const iso = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
  const us = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})(?:\s|$)/);
  if (us) {
    const year = us[3].length === 2 ? Number(`20${us[3]}`) : Number(us[3]);
    return new Date(year, Number(us[1]) - 1, Number(us[2]));
  }
  const parsed = new Date(text);
  return Number.isNaN(parsed.getTime()) ? null : new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
};

const serviceMonthIndex = (value) => {
  const text = String(value || '').trim().toUpperCase();
  if (!text) return null;
  const iso = text.match(/^(\d{4})-(\d{2})/);
  if (iso) return Number(iso[2]) - 1;
  const word = text.match(/[A-Z]+/)?.[0];
  return MONTHS.has(word) ? MONTHS.get(word) : null;
};

const serviceMonthWindow = (serviceMonth) => {
  const date = parseDateOnly(serviceMonth);
  if (!date) return null;
  const start = new Date(date.getFullYear(), date.getMonth(), 1);
  const end = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  return { start, end };
};

const firstDate = (row = {}, keys = []) => keys.map((key) => parseDateOnly(row[key])).find(Boolean) || null;

const normalizeProgram = (value = 'website') => {
  const normalized = String(value || 'website').trim().toLowerCase();
  return ['website', 'chat', 'trade'].includes(normalized) ? normalized : 'website';
};

const firstOfNextMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 1);
const firstOfCurrentMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1);

const expectedBillingStart = (program, effectiveDate) => {
  const date = parseDateOnly(effectiveDate);
  if (!date) return null;
  if (normalizeProgram(program) === 'website') return firstOfNextMonth(date);
  return date.getDate() <= 15 ? firstOfCurrentMonth(date) : firstOfNextMonth(date);
};

const explicitSourceSaysBilling = (row = {}, status) => {
  const explicit = booleanOrNull(row.isBilling ?? row.isBillable ?? row.is_billing ?? row.is_billable);
  return explicit !== null ? explicit : Boolean(PRODUCT_STATUS_CONFIG[normalizeProductStatus(status, '')]?.billing);
};

const stateFor = ({ state, billableForRound, sourceSaysBilling, reason }) => ({
  state,
  billableForRound,
  sourceSaysBilling,
  reason,
});

const hasServerBillability = (value) => (
  value
  && typeof value === 'object'
  && ['billable', 'not_billable', 'billing_conflict'].includes(value.state)
  && typeof value.billableForRound === 'boolean'
);

const startDateForRow = (row = {}) => firstDate(row, [
  'billingStartDate',
  'billingStartDateDisplay',
  'billingDate',
  'orderStartDate',
  'effectiveDate',
  'activationDate',
  'goLiveDate',
  'productSelectionDate',
  'enrollmentDate',
]);

const explicitBillingStartForRow = (row = {}) => firstDate(row, [
  'billingStartDate',
  'billingStartDateDisplay',
  'billingDate',
  'orderStartDate',
]);

const effectiveDateForRow = (row = {}) => firstDate(row, [
  'effectiveDate',
  'activationDate',
  'goLiveDate',
  'productSelectionDate',
  'enrollmentDate',
]);

const cpqWebsiteDateForRow = (row = {}) => firstDate(row, [
  'effectiveDate',
  'activationDate',
  'enrollmentDate',
  'goLiveDate',
  'productSelectionDate',
  'billingStartDate',
  'billingStartDateDisplay',
  'billingDate',
  'orderStartDate',
  'inheritedEffectiveDate',
  'inheritedAppSheetDate',
]);

const endDateForRow = (row = {}) => firstDate(row, [
  'billingEndDate',
  'billingEndDateDisplay',
  'orderEndDate',
  'cancellationDate',
  'cancelDate',
  'optOutDate',
  'terminationDate',
]);

const openBillingDateRange = (row = {}) => Boolean(startDateForRow(row) && !endDateForRow(row));

export const billingDatesAllowServiceMonth = (row = {}, serviceMonth = null) => {
  const window = serviceMonthWindow(serviceMonth);
  if (!window) return null;
  const start = startDateForRow(row);
  const end = endDateForRow(row);
  if (!start && !end) return null;
  if (start && start > window.end) return false;
  if (end && end < window.start) return false;
  return true;
};

export const serviceMonthsMatch = (rowMonth, serviceMonth) => {
  const rowDate = parseDateOnly(rowMonth);
  const serviceDate = parseDateOnly(serviceMonth);
  if (rowDate && serviceDate) {
    return rowDate.getFullYear() === serviceDate.getFullYear()
      && rowDate.getMonth() === serviceDate.getMonth();
  }
  const rowIndex = serviceMonthIndex(rowMonth);
  const serviceIndex = serviceMonthIndex(serviceMonth);
  return rowIndex !== null && serviceIndex !== null && rowIndex === serviceIndex;
};

export const statusCanBill = (status) => BILLABLE_STATUSES.has(normalizeProductStatus(status, ''));

const cpqWebsiteDateBillability = ({ row = {}, serviceMonth = null, program = 'website' } = {}) => {
  if (normalizeProgram(program) !== 'website') return null;
  const window = serviceMonthWindow(serviceMonth);
  const productDate = cpqWebsiteDateForRow(row);
  if (!window || !productDate) return null;
  return productDate < window.start;
};

export const roundBillabilityForPolicy = ({
  program = 'website',
  row = {},
  status = row.productStatus || row.status,
  serviceMonth = null,
} = {}) => {
  if (!statusCanBill(status)) return false;
  const window = serviceMonthWindow(serviceMonth);
  if (!window) return null;
  const policyStart = expectedBillingStart(program, effectiveDateForRow(row));
  const billingStart = policyStart || explicitBillingStartForRow(row);
  const billingEnd = endDateForRow(row);
  if (billingStart && billingStart > window.start) return false;
  if (billingEnd && billingEnd < window.start) return false;
  if (billingStart || billingEnd) return true;
  return null;
};

export const sourceBillability = ({ source, row = {}, status = row.productStatus || row.status, serviceMonth = null, program = 'website' } = {}) => {
  if (hasServerBillability(row.billability)) return row.billability;
  const explicit = booleanOrNull(row.isBilling ?? row.isBillable ?? row.is_billing ?? row.is_billable);
  const policyBillable = roundBillabilityForPolicy({ program, row, status, serviceMonth });
  const sourceSaysBilling = explicitSourceSaysBilling(row, status);
  if (source === 'dpe') {
    if (statusCanBill(status) && sourceSaysBilling && policyBillable === false) {
      return stateFor({
        state: 'billing_conflict',
        billableForRound: false,
        sourceSaysBilling,
        reason: 'DPE marks this product as billing, but billing policy says it should not bill this round.',
      });
    }
    const billable = explicit !== null
      ? explicit
      : policyBillable !== null
        ? policyBillable
        : sourceSaysBilling;
    return stateFor({
      state: billable ? 'billable' : 'not_billable',
      billableForRound: billable,
      sourceSaysBilling,
      reason: billable ? 'Billable for this round.' : 'Not billable for this round.',
    });
  }
  if (source === 'gm' || source === 'historical_final_gm') {
    if (sourceSaysBilling && policyBillable === false) {
      return stateFor({
        state: 'billing_conflict',
        billableForRound: false,
        sourceSaysBilling,
        reason: 'GM file marks this product as billing, but billing policy says it should not bill this round.',
      });
    }
    const billable = policyBillable !== null ? policyBillable : (explicit !== null ? explicit : sourceSaysBilling);
    return stateFor({
      state: billable ? 'billable' : 'not_billable',
      billableForRound: billable,
      sourceSaysBilling,
      reason: billable ? 'Billable for this round.' : 'Not billable for this round.',
    });
  }
  if (source === 'appsheet') {
    if (!statusCanBill(status)) return stateFor({
      state: 'not_billable',
      billableForRound: false,
      sourceSaysBilling,
      reason: 'Not billable for this round.',
    });
    if (policyBillable !== null) return stateFor({
      state: policyBillable ? 'billable' : 'not_billable',
      billableForRound: policyBillable,
      sourceSaysBilling,
      reason: policyBillable ? 'Billable for this round.' : 'Not billable for this round.',
    });
    const dateAllows = billingDatesAllowServiceMonth(row, serviceMonth);
    const billable = dateAllows === null ? sourceSaysBilling : dateAllows;
    return stateFor({
      state: billable ? 'billable' : 'not_billable',
      billableForRound: billable,
      sourceSaysBilling,
      reason: billable ? 'Billable for this round.' : 'Not billable for this round.',
    });
  }
  if (source === 'cpq') {
    const cpqDateBillable = cpqWebsiteDateBillability({ row, serviceMonth, program });
    if (cpqDateBillable !== null) return stateFor({
      state: cpqDateBillable ? 'billable' : 'not_billable',
      billableForRound: cpqDateBillable,
      sourceSaysBilling,
      reason: cpqDateBillable ? 'Billable for this round.' : 'Not billable for this round.',
    });
    // Chat and Trade rows are already scoped to the selected billing month.
    // Their explicit charge/credit direction is source truth; Website keeps
    // the activation-date rule above.
    if (explicit !== null) return stateFor({
      state: explicit ? 'billable' : 'not_billable',
      billableForRound: explicit,
      sourceSaysBilling: explicit,
      reason: explicit ? 'Billable for this round.' : 'Not billable for this round.',
    });
    if (!statusCanBill(status)) return stateFor({
      state: 'not_billable',
      billableForRound: false,
      sourceSaysBilling,
      reason: 'Not billable for this round.',
    });
    if (policyBillable !== null) return stateFor({
      state: policyBillable ? 'billable' : 'not_billable',
      billableForRound: policyBillable,
      sourceSaysBilling,
      reason: policyBillable ? 'Billable for this round.' : 'Not billable for this round.',
    });
    const dateAllows = billingDatesAllowServiceMonth(row, serviceMonth);
    const billable = dateAllows !== null
      ? dateAllows
      : row.isCurrentMonth === false
        ? false
        : row.serviceMonth && serviceMonth
          ? serviceMonthsMatch(row.serviceMonth, serviceMonth)
          : Boolean(row.isCurrentMonth || openBillingDateRange(row));
    return stateFor({
      state: billable ? 'billable' : 'not_billable',
      billableForRound: billable,
      sourceSaysBilling,
      reason: billable ? 'Billable for this round.' : 'Not billable for this round.',
    });
  }
  return explicit;
};

export const draftActionBillability = ({ fileKind, action = {}, serviceMonth = null, program = 'website' } = {}) => {
  if (hasServerBillability(action.billability)) return action.billability;
  const evidence = action.sourceEvidence?.row || action.sourceEvidence || action.targetEvidence?.row || action.targetEvidence || action;
  return sourceBillability({
    source: fileKind,
    row: {
      ...evidence,
      isBilling: action.isBilling ?? action.is_billing ?? action.isBillable ?? action.is_billable ?? evidence.isBilling ?? evidence.is_billing,
      productStatus: action.productStatus || action.status || evidence.productStatus || evidence.status,
      serviceMonth: action.serviceMonth || evidence.serviceMonth,
    },
    status: action.productStatus || action.status || evidence.productStatus || evidence.status,
    serviceMonth,
    program,
  });
};
